import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { requestNotificationPermission as requestFCMPermission } from '../firebase';

const PWAContext = createContext(null);

const NOTIFICATION_SOUND_STORAGE_KEY = 'kosha_notification_sound';

function getNotificationPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'default';
}

function getNotificationSoundEnabled() {
  try {
    const v = localStorage.getItem(NOTIFICATION_SOUND_STORAGE_KEY);
    return v !== 'false';
  } catch {
    return true;
  }
}

export function PWAProvider({ children }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(() => getNotificationPermission());
  const [notificationSoundEnabled, setNotificationSoundEnabledState] = useState(() => getNotificationSoundEnabled());

  const playNotificationSound = useCallback(() => {
    try {
      const soundUrl = `${window.location?.origin || ''}${process.env.PUBLIC_URL || ''}/notifications.mp3`;
      const audio = new Audio(soundUrl);
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (_) {}
  }, []);

  const setNotificationSoundEnabled = useCallback((enabled) => {
    setNotificationSoundEnabledState(enabled);
    try {
      localStorage.setItem(NOTIFICATION_SOUND_STORAGE_KEY, String(enabled));
    } catch (_) {}
    if (enabled) {
      playNotificationSound();
    }
  }, [playNotificationSound]);

  // Play sound when service worker sends a message (tab open but not focused)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND' && notificationSoundEnabled) {
        playNotificationSound();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [notificationSoundEnabled, playNotificationSound]);

  // Keep notification permission in sync (e.g. after user grants it)
  useEffect(() => {
    setNotificationPermission(getNotificationPermission());
    const onFocus = () => setNotificationPermission(getNotificationPermission());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const showForegroundNotification = useCallback((title, body) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      const icon = typeof window !== 'undefined' && window.location && window.location.origin
        ? `${window.location.origin}/logo192.png`
        : '/logo192.png';
      const n = new Notification(title, { body: body || '', icon });
      n.onshow = () => setTimeout(() => n.close(), 5000);
      if (notificationSoundEnabled) {
        const soundUrl = `${window.location?.origin || ''}${process.env.PUBLIC_URL || ''}/notifications.mp3`;
        const audio = new Audio(soundUrl);
        audio.volume = 0.5;
        audio.play().catch(() => {});
      }
    } catch (e) {
      console.warn('Show notification failed', e);
    }
  }, [notificationSoundEnabled]);

  const requestNotificationPermission = useCallback(async () => {
    await requestFCMPermission();
    setNotificationPermission(getNotificationPermission());
  }, []);

  const subscribeToPush = useCallback(() => requestNotificationPermission(), [requestNotificationPermission]);

  // Register Service Worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const publicUrl = process.env.PUBLIC_URL || '';
    const script = `${publicUrl}/sw.js`;

    navigator.serviceWorker
      .register(script)
      .then(() => navigator.serviceWorker.ready)
      .then((reg) => {
        setSwRegistration(reg);
      })
      .catch((err) => console.warn('Service worker registration failed:', err));
  }, []);

  // Handle install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Check if installed
  useEffect(() => {
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const standaloneIOS = window.navigator.standalone === true;

      if (standalone || standaloneIOS) {
        setIsInstalled(true);
      }
    };

    checkInstalled();
  }, []);

  // Trigger Install
  const install = useCallback(async () => {
    if (!installPrompt) return false;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setCanInstall(false);
    }

    return outcome === 'accepted';
  }, [installPrompt]);

  const value = {
    installPrompt,
    canInstall,
    isInstalled,
    install,
    swRegistration,
    notificationPermission,
    notificationSoundEnabled,
    setNotificationSoundEnabled,
    playNotificationSound,
    showForegroundNotification,
    requestNotificationPermission,
    subscribeToPush,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWA() {
  const ctx = useContext(PWAContext);
  if (!ctx) throw new Error('usePWA must be used within PWAProvider');
  return ctx;
}